import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { FAQS, IMAGES, PRICING_INTRO, PRICING_PACKS, REVIEWS } from '../data/mock';
import { usePageMeta } from '../components/Layout';
import { Reveal, SectionHeader, PricingTiers, FAQAccordion, CloseCTA, ReviewCard, PageHero } from '../components/Shared';

export default function Pricing() {
  usePageMeta(
    'Pricing — Memberships & Class Packs',
    'PITFIT pricing in Uptown Dallas: $15 first class or 2 weeks unlimited for $50 (new clients), memberships from $165/month, class packs, and $36 drop-ins. See every option.'
  );

  const pricingFaqs = FAQS.filter((f) => f.cat === 'Pricing');

  return (
    <>
      <PageHero
        tag="Memberships & Packs"
        title="Start at $15. Then pick your pace."
        sub="First-timers start with a $15 class or two unlimited weeks for $50. After that: memberships, class packs or drop-ins — every option below, no surprises."
        img={IMAGES.sweat}
      />

      {/* FIRST-TIME OFFERS */}
      <section className="section-pad !pb-0">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="mono-label mb-5">New to PITFIT? Start here</p>
          <div className="grid md:grid-cols-2 gap-6">
            {PRICING_INTRO.map((o, i) => (
              <Reveal key={o.id} delay={i * 80}>
                <div className="glass-card p-7 md:p-8 h-full flex flex-col" style={{ borderColor: 'rgba(125,167,255,0.4)', boxShadow: '0 0 30px rgba(70,127,247,0.12)' }} data-testid={`intro-offer-${o.id}`}>
                  <p className="mono-label">{o.name}</p>
                  <div className="flex items-baseline gap-2 mt-4">
                    <span className="font-display font-bold text-5xl text-white">${o.price}</span>
                    <span className="text-muted-pf font-mono-pf text-[13px] uppercase tracking-[0.1em]">{o.unit}</span>
                  </div>
                  <p className="text-muted-pf text-[15px] mt-4 flex-1">{o.desc}</p>
                  <Link to={o.to} className="btn-primary w-full mt-6 justify-center">{o.cta} <ArrowRight size={16} /></Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEMBERSHIPS */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="Memberships" title="Train on a rhythm." sub="Every membership covers every class type on the schedule — HIIT and HYROX alike." />
          <PricingTiers />
          <p className="font-mono-pf text-[12px] uppercase tracking-[0.12em] text-muted-pf text-center mt-9">
            All memberships · 3-month minimum commitment · freeze available · cancel with 30 days' notice
          </p>
        </div>
      </section>

      {/* CLASS PACKS + DROP-IN */}
      <section className="section-pad !pt-0">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="Class Packs & Drop-Ins" title="No subscription? No problem." sub="Buy classes when you need them — packs never force a monthly rhythm." />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {PRICING_PACKS.map((p, i) => (
              <Reveal key={p.id} delay={i * 70}>
                <div className="glass-card glass-card-hover p-6 h-full flex flex-col" data-testid={`pack-${p.id}`}>
                  <p className="mono-label">{p.name}</p>
                  <div className="flex items-baseline gap-2 mt-3">
                    <span className="font-display font-bold text-4xl text-white">${p.price}</span>
                    <span className="text-muted-pf font-mono-pf text-[12.5px]">{p.unit}</span>
                  </div>
                  <p className="text-muted-pf text-[14.5px] mt-3 flex-1">{p.note}</p>
                  <Link to="/buy" className="btn-ghost w-full mt-5 justify-center !min-h-[44px]">Buy Now <ArrowRight size={15} /></Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="Every Plan Includes" title="No tiers of treatment." sub="Whether you drop in once or live in the PIT, class is class — same coaching, same energy, same intentionally sized rooms." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              ['Coached classes', 'A certified coach runs every minute — form, pacing, motivation.'],
              ['All equipment', 'Weights, ropes, sleds, TRX — everything\u2019s in the PIT.'],
              ['Programming', 'Weekly split programmed by founder Chris Wiese and his coaching team.'],
              ['Free parking', 'Park in the Travis Walk garage — the studio\u2019s inside it.'],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 70}>
                <div className="glass-card glass-card-hover p-6 h-full">
                  <h3 className="font-display font-semibold text-white text-lg">{t}</h3>
                  <p className="text-muted-pf text-[14.5px] mt-2">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING FAQ */}
      <section className="section-pad">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="Pricing Questions" title="Zero surprises." center />
          <FAQAccordion faqs={pricingFaqs} />
          <div className="mt-8 text-center">
            <Link to="/faq" className="btn-quiet">All frequently asked questions <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="section-pad !pt-0">
        <div className="max-w-2xl mx-auto px-5 lg:px-8">
          <Reveal>
            <ReviewCard review={REVIEWS[3]} />
          </Reveal>
        </div>
      </section>

      <CloseCTA />
    </>
  );
}
